/* global google */
import i18n from "i18next";
import { groupBy, keyBy, uniq } from "lodash";
import Colors from "../themes/Colors";
import { VENUE_CATEGORIES, OTHER_VENUE_CATEGORY } from "../constants/venueCategories";

// Legend entries: every configured category plus "Others" last.
export const VENUE_CATEGORY_LIST = [...VENUE_CATEGORIES, OTHER_VENUE_CATEGORY];

// OpenOOH root id -> category, from each category's rootIds.
const CATEGORY_BY_ROOT = {};
VENUE_CATEGORIES.forEach((category) =>
  category.rootIds.forEach((rootId) => {
    CATEGORY_BY_ROOT[rootId] = category;
  })
);

// Taxonomy (venue-types-list) by id, for the label helpers called outside React.
// redux/index.js keeps it in sync with display.venueTypes.
let catalog = null;
let catalogById = {};
export const setVenueCatalog = (venueTypes) => {
  if (venueTypes === catalog) return;
  catalog = venueTypes;
  catalogById = keyBy(venueTypes || [], "id");
};

// Node name in the user's language: name_en for English, the Spanish name
// otherwise (catalog nodes carry it as `name`, path nodes as `name_es`).
export const getVenueName = (node) =>
  (node && (/^en/.test(i18n.language) ? node.name_en : node.name_es || node.name)) || null;

// Root-to-leaf nodes of a display ({id, name_en, name_es} or catalog nodes).
// Accepts the old int[] path (names from the catalog). Without a path field it
// derives the ids from the node id, since each OpenOOH level appends two digits
// (10105 -> 101 -> 1), and gives none when the node is not in the catalog.
export const getVenuePathNodes = (display) => {
  if (!display) return [];
  const path = display.open_ooh_venue_type_path;
  if (Array.isArray(path)) {
    return path.map((item) => (item && typeof item === "object" ? item : catalogById[item] || { id: Number(item) }));
  }
  const id = Number(display.open_ooh_venue_type_id);
  if (!catalogById[id]) return [];
  const ids = [];
  for (let s = String(Math.trunc(id)); s; s = s.slice(0, -2)) ids.unshift(Number(s));
  return ids.map((venueId) => catalogById[venueId] || { id: venueId });
};

// Root-to-leaf OpenOOH ids of a display, e.g. [1, 106, 10602].
export const getVenuePath = (display) => getVenuePathNodes(display).map((node) => Number(node.id));

// Type of a display: the root of its node. null without node.
export const getVenueTypeLabel = (display) => getVenueName(getVenuePathNodes(display)[0]);

// Subtype of a display: its most specific node below the root. null without one.
export const getVenueSubtypeLabel = (display) => {
  const nodes = getVenuePathNodes(display);
  return nodes.length > 1 ? getVenueName(nodes[nodes.length - 1]) : null;
};

// Whole path, e.g. "Tránsito › Aeropuerto › Puertas de embarque". null without node.
// A name repeated one level down (Centro Corporativo / Coworking › Centro Corporativo / Coworking) shows once.
export const getVenuePathLabel = (display) =>
  getVenuePathNodes(display)
    .map(getVenueName)
    .filter((name, i, names) => name && name !== names[i - 1])
    .join(" › ") || null;

// Grouping keys, never deeper than level 2: the root for the category (type)
// and the level 2 node for the subcategory (subtype). "No category" without
// node, "No subcategory" when the display is assigned to a root only.
export const getVenueGroupLabels = (display, t) => {
  const [root, level2] = getVenuePathNodes(display);
  const type = getVenueName(root);
  if (!type) return { type: t("No category"), subtype: t("No category") };
  return { type, subtype: getVenueName(level2) || t("No subcategory") };
};

// Legend name of a category: the names of its roots, or its translation key.
export const getVenueCategoryLabel = (category, t) =>
  category.labelKey
    ? t(category.labelKey)
    : category.rootIds.map((id) => getVenueName(catalogById[id])).filter(Boolean).join(" / ");

// The type filter (location_type) narrows what the category and subcategory
// filters show, not what they send. A node is of the types when its
// location_types has one of them. Visible: the nodes of the types, the level 2
// nodes with a child of the types, and the roots with any of those below.
// Without types everything is visible.
export const getVisibleVenueIds = (venueTypes, types) => {
  const nodes = venueTypes || [];
  if (!types || !types.length) return new Set(nodes.map((node) => node.id));
  const ofTypes = (node) => (node.location_types || []).some((type) => types.includes(type));
  const visible = new Set(nodes.filter((node) => node.level > 1 && ofTypes(node)).map((node) => node.id));
  nodes.forEach((node) => {
    if (node.level === 3 && visible.has(node.id)) visible.add(node.parent_id);
  });
  nodes.forEach((node) => {
    if (node.level > 1 && visible.has(node.id)) visible.add(node.root_id);
  });
  return visible;
};

// Drops from a filter selection what the types hide. Without catalog it keeps
// the selection, which may come from a link opened before the catalog arrives.
export const pruneVenueSelection = (selection, venueTypes, types) => {
  if (!venueTypes || !venueTypes.length) return selection;
  const visible = getVisibleVenueIds(venueTypes, types);
  return selection.filter((id) => visible.has(Number(id)));
};

// Ids for location_sub_type[] from a filter selection (roots and subtypes).
// The API matches them exactly, so for each selected root this sends its
// highest selected subtypes (or the root itself) plus all their descendants.
// Without catalog it sends nothing.
export const expandVenueSelection = (selection, venueTypes) => {
  const byId = keyBy(venueTypes || [], "id");
  const children = groupBy(venueTypes || [], "parent_id");
  const picked = uniq((selection || []).map(Number)).filter((id) => byId[id]);
  const subtypes = picked.filter((id) => byId[id].level > 1);
  const hasPickedAncestor = (id) => {
    for (let parent = byId[id].parent_id; byId[parent]; parent = byId[parent].parent_id) {
      if (subtypes.includes(parent)) return true;
    }
    return false;
  };
  const tops = subtypes.filter((id) => !hasPickedAncestor(id));
  const bareRoots = uniq(picked.map((id) => byId[id].root_id)).filter(
    (root) => !tops.some((id) => byId[id].root_id === root)
  );
  const expand = (id) => [id, ...(children[id] || []).flatMap((child) => expand(child.id))];
  return [...bareRoots, ...tops].flatMap(expand);
};

// Category (color, default icon, label) of a display; "Others" when unknown.
export const getVenueCategory = (display) => {
  const root = (display && display.open_ooh_venue_type_id_parent) || getVenuePath(display)[0];
  return CATEGORY_BY_ROOT[root] || OTHER_VENUE_CATEGORY;
};

// Most specific configured icon: walks the path from leaf to root (roots count
// too, since a category can group several), falling back to the category icon.
export const getVenueIcon = (display) => {
  const category = getVenueCategory(display);
  const subtypeIcons = category.subtypeIcons || {};
  const path = getVenuePath(display);
  for (let i = path.length - 1; i >= 0; i--) {
    if (subtypeIcons[path[i]]) return subtypeIcons[path[i]];
  }
  return category.icon;
};

const svgIcon = (width, height, body, anchorX, anchorY, pxPerUnit = 1) => ({
  url:
    "data:image/svg+xml;charset=UTF-8," +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${body}</svg>`
    ),
  size: new google.maps.Size(width * pxPerUnit, height * pxPerUnit),
  scaledSize: new google.maps.Size(width * pxPerUnit, height * pxPerUnit),
  anchor: new google.maps.Point(anchorX * pxPerUnit, anchorY * pxPerUnit),
});

// Looping "radar" pulse for screens on the cart: the shape grows and fades out.
// SVG animations only play because venue markers are not optimized (DisplayMarker).
const pulse = (shape, attrs, grow) =>
  `<${shape} ${attrs} fill="none" stroke-width="1.5">` +
  grow
    .map(([name, from, to]) => `<animate attributeName="${name}" values="${from};${to}" dur="1.6s" repeatCount="indefinite"/>`)
    .join("") +
  '<animate attributeName="stroke-opacity" values="0.9;0" dur="1.6s" repeatCount="indefinite"/>' +
  `</${shape}>`;

// Point marker: a colored dot on a 24px canvas, with room for the pulse around it.
const buildPointIcon = (color, isOnCart, isHovered) => {
  const radius = isHovered ? 8 : 6;
  const body =
    (isOnCart ? pulse("circle", `cx="12" cy="12" r="${radius}" stroke="${color}"`, [["r", radius, 11]]) : "") +
    `<circle cx="12" cy="12" r="${radius}" fill="${color}" stroke="${Colors.white}" stroke-width="1.5"/>`;
  return svgIcon(24, 24, body, 12, 12);
};

// Detail marker: a classic map drop, a circular head (center (18, 24.5), r 17)
// tapering to the tip (36 units = 34px wide). The pin sits DETAIL_MAX_LIFT units
// below the top of the canvas so it can rise on hover, and the anchor is the tip
// at rest (18, 52), on the screen location.
const DETAIL_PX_PER_UNIT = 34 / 36;
const DETAIL_MAX_LIFT = 6;
const DETAIL_LIFT_STEPS = 6;
const DETAIL_MARKER_PATH = "M18 52 C18 52 1 37 1 24.5 A17 17 0 0 1 35 24.5 C35 37 18 52 18 52 Z";
// Longest side of the icon, centered on the head. The pin icons leave some
// padding in their viewBox, so 23 still keeps every glyph inside the head.
const DETAIL_ICON_SIZE = 23;

// Under the pin: a pulsing colored mark when the screen is on the cart, or a
// shadow that grows as the pin rises.
const buildGround = (color, isOnCart, lift) => {
  if (isOnCart) {
    const ellipse = 'cx="18" cy="52" rx="9" ry="3.4"';
    return (
      pulse("ellipse", `${ellipse} stroke="${color}"`, [["rx", 9, 16], ["ry", 3.4, 6]]) +
      `<ellipse ${ellipse} fill="${color}" fill-opacity="0.85" stroke="${Colors.white}" stroke-width="1"/>`
    );
  }
  return lift ? `<ellipse cx="18" cy="52" rx="${4 + lift * 0.4}" ry="1.8" fill="#000" fill-opacity="${0.05 * lift}"/>` : "";
};

const buildDetailIcon = (color, icon, isOnCart, liftStep) => {
  const [width, height, , , svgPath] = icon.icon;
  const scale = DETAIL_ICON_SIZE / Math.max(width, height);
  const lift = (liftStep / DETAIL_LIFT_STEPS) * DETAIL_MAX_LIFT;
  const body =
    buildGround(color, isOnCart, lift) +
    `<g transform="translate(0 ${-lift})">` +
    `<path d="${DETAIL_MARKER_PATH}" fill="${color}" ` +
    `stroke="${Colors.white}" stroke-width="2" stroke-linejoin="round"/>` +
    `<path fill="${Colors.white}" d="${[].concat(svgPath).join(" ")}" ` +
    `transform="translate(${18 - (width * scale) / 2} ${24.5 - (height * scale) / 2}) scale(${scale})"/>` +
    "</g>";
  return svgIcon(36, 59, body, 18, 52, DETAIL_PX_PER_UNIT);
};

// Cached so PureComponent markers get the same icon reference across renders.
// Keyed by icon prefix + iconName, so custom icons need a unique iconName.
const markerIconCache = new Map();

// Map marker icon for a display: a colored dot in "point" mode (bigger on
// hover), a colored pin with the category/subtype icon in "detail" mode.
// lift (0..1) is how far a detail pin has risen; it defaults to fully risen on
// hover, and DisplayMarker passes the values in between while animating.
export const getVenueMarkerIcon = (display, { variant, isOnCart, isHovered, lift = isHovered ? 1 : 0 }) => {
  const { color } = getVenueCategory(display);
  const isPoint = variant === "point";
  const icon = isPoint ? null : getVenueIcon(display);
  const liftStep = Math.round(lift * DETAIL_LIFT_STEPS);
  const state = isPoint ? `point-${!!isHovered}` : `detail-${liftStep}`;
  const key = [color, icon && `${icon.prefix}-${icon.iconName}`, !!isOnCart, state].join("|");
  if (!markerIconCache.has(key)) {
    markerIconCache.set(
      key,
      isPoint ? buildPointIcon(color, isOnCart, isHovered) : buildDetailIcon(color, icon, isOnCart, liftStep)
    );
  }
  return markerIconCache.get(key);
};
